import "./FileUpload.scss";
import { useEffect, useState, useRef, useContext } from "react";
import {
  FileUploadContext,
  FileUploadContextProps,
} from "../../Contexts/FileUploadContext";
import { Placeholder, Text } from "../Typography";
import { Button } from "../Button";
import PdfLogo from "../../assets/pdf-icon.png";
import useFileupload from "../../hooks/useFileupload";
import useApi from "../../hooks/useApi";
import { toast } from "react-toastify";
import { LoadingUpload } from "../Loading";
import useResponsive from "../../hooks/useResponsive";
import FileCard from "../FileCard/FileCard";
import { OrderContext, OrderContextProps } from "../../Contexts/OrderContext";
import promiseProgress from "../../utils/promiseProgress";

const PDF_TYPE = "application/pdf";
const IMAGE_TYPE = ["image/jpeg", "image/jpg", "image/png", "image/bmp"];
const VALID_EXTENSIONS = [PDF_TYPE, ...IMAGE_TYPE];

export const FileUpload = () => {
  const { fileUpload }: FileUploadContextProps =
    useContext<any>(FileUploadContext);
  const { getCalculatedPrice, getCalculatedShippingCost }: OrderContextProps =
    useContext<any>(OrderContext);
  const { uploadedFiles, setUploadedFiles } = fileUpload;

  const { fbFileUpload, fbFileDelete, fbGetUploadedFiles, progress: fbProgress } = useFileupload();
  const { validatePDF } = useApi();
  const [hovering, setHovering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMultipleFiles, setIsMultipleFiles] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileUploadRef = useRef<any>();

  const { isDesktopOrLaptop } = useResponsive();

  const getUploadedFiles = async () => {
    const files: any = await fbGetUploadedFiles();

    setUploadedFiles(files);
  };

  useEffect(() => {
    getUploadedFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const totalPages = uploadedFiles.reduce(
      (acc: number, item: any) => (acc += item.pdfPages),
      0
    );

    getCalculatedPrice({ totalPages });
    getCalculatedShippingCost({ totalPages });
  }, [uploadedFiles]);

  // Removed storage listener - each tab should be independent now

  const uploadFileList = async (fileList: any) => {
    setLoading(true);
    setIsMultipleFiles(fileList.length > 1);
    const promiseList = fileList.map(
      (file: any) =>
        new Promise(async (resolve, reject) => {
          const isPDF = file.type === PDF_TYPE;
          const isNotValid = VALID_EXTENSIONS.indexOf(file.type) === -1;

          if (isNotValid) {
            reject({
              file,
              error: "Extensión de archivo inválido! Favor utilizar .pdf",
            });
          } else {
            const pdfPages = isPDF ? await validatePDF(file) : 1;

            if (pdfPages > 0) {
              const metadata: any = await fbFileUpload(file, pdfPages);

              const fileMetadata = {
                ...metadata,
                ...pdfPages,
              };

              if (fileMetadata) {
                resolve({
                  file,
                });
              } else {
                reject({
                  file,
                  error: "Error en el upload del archivo.",
                });
              }
            } else {
              reject({
                file,
                error: "PDF sin páginas validas.",
              });
            }
          }
        })
    );

    // Update progress per each file/promise
    promiseProgress(promiseList, (p: any) => {
      setProgress(Number(p.toFixed(0)));
    });

    // When all promises are processed
    Promise.all(promiseList)
      .then((success: any) => {
        setProgress(100);

        toast.success(
          <div>
            {success.length === 1 ? "Archivo añadido:" : "Archivos añadidos:"}
            <ul>
              {success.map((item: any, i: number) => (
                <li key={i}>{item.file.name}</li>
              ))}
            </ul>
          </div>
        );
        getUploadedFiles();
      })
      .catch((error: any) => {
        toast.success(
          `Upload error: ${error.map((item: any) => item.file.name)}`
        );
      })
      .finally(() => {
        setTimeout(() => {
          setLoading(false)
        }, 250)
      });
  };

  // Simulate upload progress
  useEffect(() => {
    let intervalId: any = undefined;

    if (loading) {
      intervalId = setInterval(() => {
        if (progress < 100) {
          setProgress(progress + 1);
        }
      }, 200);
    }

    return () => clearInterval(intervalId);
  }, [loading, progress]);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const fileUploadedList = Array.from(e?.target[0]?.files);

    uploadFileList(fileUploadedList);
  };

  const onChangeFileUpload = (e: any) => {
    const fileUploadedList = Array.from(e?.target?.files);

    uploadFileList(fileUploadedList);
  };

  const onDropFile = (e: any) => {
    e.preventDefault();
    setHovering(false);

    const fileUploadedList = Array.from(e.dataTransfer.items)
      .filter((file: any) => file?.kind === "file")
      .map((file: any) => file.getAsFile());

    uploadFileList(fileUploadedList);
  };

  const onDragOverFile = (e: any) => {
    e.preventDefault();
    setHovering(true);
  };

  const onDragEndFile = (e: any) => {
    setHovering(false);
  };

  const onClickDelete = async (uploadedFile: any) => {
    await fbFileDelete(uploadedFile);
    toast.success(`Archivo eliminado: ${uploadedFile.name}`);

    getUploadedFiles();
  };

  const onClickFileUpload = () => {
    fileUploadRef.current.click();
  };

  return (
    <div className="file-upload">
      {loading ? (
        <div className="loading-container">
          <LoadingUpload progress={isMultipleFiles ? progress : fbProgress} />
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div
            id="drop-zone"
            onDrop={onDropFile}
            onDragOver={onDragOverFile}
            onDragLeave={onDragEndFile}
            className="drag-drop-area"
          >
            <img alt="" src={PdfLogo} />

            {isDesktopOrLaptop && (
              <Text>
                {hovering
                  ? "Suelta en este área"
                  : "Arrastre aquí sus archivos PDF"}
              </Text>
            )}

            {!hovering && <Placeholder>250 MB tamaño máximo</Placeholder>}

            {!hovering && (
              <>
                <input
                  ref={fileUploadRef}
                  type="file"
                  onChange={onChangeFileUpload}
                  style={{ display: "none" }}
                  multiple={true}
                  accept={VALID_EXTENSIONS.join(", ")}
                />
                <Button onClick={onClickFileUpload}>
                  Seleccionar Archivos
                </Button>
              </>
            )}
          </div>
        </form>
      )}

      <div className="uploaded-files">
        {uploadedFiles.map((uploadedFile: any, i: number) => (
          <FileCard
            key={i}
            name={uploadedFile.name}
            url={uploadedFile.downloadUrl}
            size={uploadedFile.size}
            onClickDelete={() => onClickDelete(uploadedFile)}
          />
        ))}
      </div>
    </div>
  );
};
